// CommitDetail.tsx — one commit opened: what it said, what it changed, and
// what that actually did.
//
// A diff answers "what changed" precisely and "what did this do" not at all.
// The message is the author's claim about the second question, and it is
// written before the change is finished as often as after. So the third thing
// here is docdaemon's reading of the diff itself — generated on request, cached
// forever afterwards because a commit is immutable and explaining it twice buys
// the same answer at the same price.
//
// The explanation is behind a button rather than generated on open: most
// commits get looked at and not read, and a panel that spends money every time
// somebody expands a row is a panel nobody should leave open.
import { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { BsStars } from 'react-icons/bs';
import { Markdown, StatusBadge } from 'common';
import { explainCommit, fetchCommit } from '../api';
import type { CommitDetail as Commit, CommitExplanation } from '../api';
import { absoluteTime, relativeTime, shortSha } from '../format';
import CodeBlock from './CodeBlock';

export default function CommitDetail({ owner, name, sha }: {
  owner: string;
  name: string;
  sha: string;
}) {
  const [commit, setCommit] = useState<Commit | null>(null);
  const [explanation, setExplanation] = useState<CommitExplanation | null>(null);
  const [error, setError] = useState('');
  const [explaining, setExplaining] = useState(false);
  const [explainError, setExplainError] = useState('');

  useEffect(() => {
    let live = true;
    setCommit(null);
    setExplanation(null);
    setError('');
    setExplainError('');
    void (async () => {
      try {
        const data = await fetchCommit(owner, name, sha);
        if (!live) return;
        setCommit(data.commit);
        // Rides along when it is already cached, so a commit read before
        // opens with its prose already there and no button to press.
        setExplanation(data.explanation ?? null);
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Failed to load the commit');
      }
    })();
    return () => { live = false; };
  }, [owner, name, sha]);

  const explain = async () => {
    setExplaining(true);
    setExplainError('');
    try {
      setExplanation(await explainCommit(owner, name, sha));
    } catch (err) {
      setExplainError(err instanceof Error ? err.message : 'Failed to explain the commit');
    } finally {
      setExplaining(false);
    }
  };

  if (error) return <Alert variant="danger" className="m-0">{error}</Alert>;
  if (!commit) {
    return <div className="d-flex justify-content-center py-3"><Spinner animation="border" size="sm" /></div>;
  }

  return (
    <div className="commit-detail">
      {/* The message as the author wrote it, wrapping preserved: a commit body
          is written to line breaks and reflowing it loses the shape. */}
      <pre className="commit-message">{commit.message.trim()}</pre>

      <div className="small text-muted mb-3">
        <code>{shortSha(commit.sha)}</code>
        {' · '}{commit.author_name}
        {' · '}<span title={absoluteTime(commit.committed_at)}>{relativeTime(commit.committed_at)}</span>
        {' · '}<span className="text-success">+{commit.additions}</span>
        {' '}<span className="text-danger">−{commit.deletions}</span>
        {' across '}{commit.files.length} file{commit.files.length === 1 ? '' : 's'}
        {commit.by_docdaemon && (
          <StatusBadge tone="info" className="ms-2" uppercase={false}>docdaemon</StatusBadge>
        )}
      </div>

      {explainError && <Alert variant="warning" className="py-2 small">{explainError}</Alert>}

      {explanation ? (
        <div className="mb-3">
          <Markdown className="prose-column">{explanation.summary}</Markdown>
          <p className="small text-muted mb-0">
            Read from the diff by <code>{explanation.model}</code>
            {' '}<span title={absoluteTime(explanation.created_at)}>{relativeTime(explanation.created_at)}</span>
          </p>
        </div>
      ) : (
        <Button
          variant="outline-primary"
          size="sm"
          className="mb-3"
          onClick={() => void explain()}
          disabled={explaining || !commit.diff}
          title={commit.diff
            ? 'Read the diff and say what it did'
            : 'This commit has no readable diff to explain'}
        >
          <span className="d-inline-flex align-items-center gap-2">
            {explaining ? <Spinner animation="border" size="sm" /> : <BsStars />}
            {explaining ? 'Reading the diff…' : 'Explain this commit'}
          </span>
        </Button>
      )}

      {commit.diff_truncated && (
        <Alert variant="warning" className="py-2 small">
          This diff is larger than the viewer will fetch — you are reading the beginning of it.
        </Alert>
      )}
      {commit.diff
        // A diff is a language of its own, and hljs has the grammar: the
        // markers, hunk headers and file lines carry more than the code
        // inside them does at this size.
        ? <CodeBlock className="commit-diff" code={commit.diff} language="diff" />
        : <p className="text-muted small m-0">Gitea returned no diff for this commit.</p>}
    </div>
  );
}
